import { z } from 'zod';

/**
 * Shared response schemas. Resource schemas use `looseObject` so new backend fields never break the UI;
 * only fields the frontend reads are declared.
 */

/** Money arrives as JSON numbers (2dp). Accept numeric strings too and always yield a number. */
export const moneySchema = z
  .union([z.number(), z.string().regex(/^-?\d+(\.\d+)?$/)])
  .transform(Number);

export const roleSchema = z.enum(['super_admin', 'admin', 'cashier']);
export type Role = z.infer<typeof roleSchema>;

export const userSchema = z.looseObject({
  id: z.number(),
  username: z.string(),
  name: z.string(),
  role: roleSchema,
  active: z.boolean(),
  must_change_password: z.boolean(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
});
export type User = z.infer<typeof userSchema>;

export const paginationMetaSchema = z.looseObject({
  current_page: z.number(),
  per_page: z.number(),
  total: z.number(),
  last_page: z.number(),
  from: z.number().nullish(),
  to: z.number().nullish(),
});
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;

/** `{ data: T[], links, meta }` envelope used by every list endpoint. */
export function paginatedSchema<T extends z.ZodType>(item: T) {
  return z.looseObject({ data: z.array(item), meta: paginationMetaSchema });
}
export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
}

/** `{ data: T }` envelope for single resources. */
export function itemSchema<T extends z.ZodType>(item: T) {
  return z.looseObject({ data: item });
}

export const messageSchema = z.looseObject({ message: z.string() });

/** Common list params. Endpoint modules extend this with their filters. */
export interface ListParams {
  page?: number;
  per_page?: number;
}

/** Drop undefined / null / '' so they never reach the query string. */
export function cleanParams<T extends object>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as Partial<T>;
}
